import React from 'react';
import {
  Check,
  Edit2,
  ShoppingCart,
  Trash2,
  Plus,
  Minus,
  Clock,
  AlertTriangle
} from 'lucide-react';
import { ABLE_COLORS } from '../../../constants';
import { computeFreshness } from '../../../hooks/useInventory';

// Freshness status colors and labels
const FRESHNESS_CONFIG = {
  fresh: { color: '#4CAF50', bgColor: '#E8F5E9', label: 'Fresh', icon: null },
  'use-soon': { color: '#FF9800', bgColor: '#FFF3E0', label: 'Use soon', icon: Clock },
  expiring: { color: '#F44336', bgColor: '#FFEBEE', label: 'Use today!', icon: AlertTriangle },
  expired: { color: '#9E9E9E', bgColor: '#F5F5F5', label: 'Expired', icon: AlertTriangle },
  unknown: { color: ABLE_COLORS.textMuted, bgColor: 'transparent', label: '', icon: null }
};

/**
 * InventoryItem - Single pantry item with inline editing
 * 
 * Features:
 * - Display item name with edit icon
 * - Category pill badge
 * - Quantity as text, tap to reveal +/- buttons
 * - Shopping cart icon to add to shopping list
 * - Trash icon to remove
 * - Inline edit form for name, quantity, unit, category, dates
 */
export default function InventoryItem({
  item,
  isEditing,
  editName,
  editQtyNumber,
  editQtyUnit,
  editCategory,
  editPurchaseDate,
  editOpenedDate,
  editExpiryDate,
  onEditNameChange,
  onEditQtyNumberChange,
  onEditQtyUnitChange,
  onEditCategoryChange,
  onEditPurchaseDateChange,
  onEditOpenedDateChange,
  onEditExpiryDateChange,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onAddToShopping,
  onRemove,
  onIncrement,
  onDecrement,
  isOnShoppingList,
  categories,
  categoryConfig,
  unitOptions,
  isLastItem,
  hideCategory = false
}) {
  // State to show/hide quantity edit buttons
  const [showQtyButtons, setShowQtyButtons] = React.useState(false);
  
  const config = categoryConfig[item.category] || categoryConfig.other;
  const freshness = computeFreshness(item);
  const freshnessConfig = FRESHNESS_CONFIG[freshness.status] || FRESHNESS_CONFIG.unknown;
  const FreshnessIcon = freshnessConfig.icon;

  // Parse quantity into number and unit for display
  const parseQtyNumber = (qty) => {
    if (!qty) return '1';
    const match = qty.match(/^([\d.]+)/);
    return match ? match[1] : '1';
  };

  const parseQtyUnit = (qty) => {
    if (!qty) return 'units';
    const unit = qty.replace(/^[\d.]+\s*/, '').trim();
    return unit || 'units';
  };

  // Inline edit form (when editing)
  if (isEditing) {
    return (
      <div
        style={{
          padding: '12px 16px',
          borderBottom: !isLastItem ? `1px solid ${ABLE_COLORS.borderLight}` : 'none',
          boxSizing: 'border-box',
          width: '100%'
        }}
      >
        {/* Row 1: Name */}
        <input
          type="text"
          value={editName}
          onChange={(e) => onEditNameChange(e.target.value)}
          placeholder="Item name"
          autoFocus
          style={{
            width: '100%',
            padding: '10px',
            border: `1px solid ${ABLE_COLORS.border}`,
            borderRadius: ABLE_COLORS.radiusSmall,
            fontSize: '16px',
            outline: 'none',
            marginBottom: '8px',
            boxSizing: 'border-box'
          }}
        />
        {/* Row 2: Qty + Unit + Category - wraps on mobile */}
        <div className="inv-edit-row" style={{ display: 'flex', gap: '6px', marginBottom: '8px', flexWrap: 'wrap' }}>
          <input
            type="number"
            value={editQtyNumber}
            onChange={(e) => onEditQtyNumberChange(e.target.value)}
            placeholder="Qty"
            style={{
              width: '60px',
              padding: '8px',
              border: `1px solid ${ABLE_COLORS.border}`,
              borderRadius: ABLE_COLORS.radiusSmall,
              fontSize: '16px',
              outline: 'none'
            }}
          />
          <select
            value={editQtyUnit}
            onChange={(e) => onEditQtyUnitChange(e.target.value)}
            style={{
              padding: '8px',
              border: `1px solid ${ABLE_COLORS.border}`,
              borderRadius: ABLE_COLORS.radiusSmall,
              fontSize: '14px',
              outline: 'none',
              background: ABLE_COLORS.neutral
            }}
          >
            {unitOptions.map(opt => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <select
            value={editCategory}
            onChange={(e) => onEditCategoryChange(e.target.value)}
            className="inv-edit-category"
            style={{
              flex: 1,
              minWidth: '100px',
              padding: '8px',
              border: `1px solid ${categoryConfig[editCategory]?.color}40`,
              borderRadius: ABLE_COLORS.radiusSmall,
              fontSize: '12px',
              fontWeight: '600',
              outline: 'none',
              background: `${categoryConfig[editCategory]?.color}15`,
              color: categoryConfig[editCategory]?.color
            }}
          >
            {categories.map(cat => (
              <option key={cat} value={cat}>
                {categoryConfig[cat]?.label || cat}
              </option>
            ))}
          </select>
        </div>
        {/* Row 3: Date fields - Purchased, Opened, Expires */}
        <div style={{ display: 'flex', gap: '6px', marginBottom: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flex: 1, minWidth: '120px' }}>
            <label style={{ fontSize: '11px', color: ABLE_COLORS.textMuted, whiteSpace: 'nowrap' }}>Bought:</label>
            <input
              type="date"
              value={editPurchaseDate || ''}
              onChange={(e) => onEditPurchaseDateChange(e.target.value)}
              style={{
                flex: 1,
                padding: '6px',
                border: `1px solid ${ABLE_COLORS.border}`,
                borderRadius: ABLE_COLORS.radiusSmall,
                fontSize: '12px',
                outline: 'none',
                minWidth: '100px',
                color: editPurchaseDate ? ABLE_COLORS.textPrimary : ABLE_COLORS.textMuted
              }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flex: 1, minWidth: '120px' }}>
            <label style={{ fontSize: '11px', color: ABLE_COLORS.textMuted, whiteSpace: 'nowrap' }}>Opened:</label>
            <input
              type="date"
              value={editOpenedDate || ''}
              onChange={(e) => onEditOpenedDateChange(e.target.value)}
              style={{
                flex: 1,
                padding: '6px',
                border: `1px solid ${ABLE_COLORS.border}`,
                borderRadius: ABLE_COLORS.radiusSmall,
                fontSize: '12px',
                outline: 'none',
                minWidth: '100px',
                color: editOpenedDate ? ABLE_COLORS.textPrimary : ABLE_COLORS.textMuted
              }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flex: 1, minWidth: '120px' }}>
            <label style={{ fontSize: '11px', color: ABLE_COLORS.textMuted, whiteSpace: 'nowrap' }}>Expires:</label>
            <input
              type="date"
              value={editExpiryDate || ''}
              onChange={(e) => onEditExpiryDateChange(e.target.value)}
              style={{
                flex: 1,
                padding: '6px',
                border: `1px solid ${ABLE_COLORS.border}`,
                borderRadius: ABLE_COLORS.radiusSmall,
                fontSize: '12px',
                outline: 'none',
                minWidth: '100px',
                color: editExpiryDate ? ABLE_COLORS.textPrimary : ABLE_COLORS.textMuted
              }}
            />
          </div>
        </div>
        {/* Row 4: Save + Cancel */}
        <div style={{ display: 'flex', gap: '6px' }}>
          <button
            onClick={onSaveEdit}
            style={{
              flex: 1,
              padding: '10px',
              background: ABLE_COLORS.primary,
              border: 'none',
              borderRadius: ABLE_COLORS.radiusSmall,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '4px',
              color: 'white',
              fontWeight: '600',
              fontSize: '14px'
            }}
          >
            <Check size={14} />
            Save
          </button>
          <button
            onClick={onCancelEdit}
            style={{
              padding: '10px 14px',
              background: ABLE_COLORS.bgSubtle,
              border: 'none',
              borderRadius: ABLE_COLORS.radiusSmall,
              cursor: 'pointer',
              color: ABLE_COLORS.textMuted,
              fontWeight: '500',
              fontSize: '14px'
            }}
          >
            Cancel
          </button>
        </div>
        <style>{`
          @media (max-width: 360px) {
            .inv-edit-category { flex-basis: 100% !important; }
          }
        `}</style>
      </div>
    );
  }

  // Normal display (not editing)
  return (
    <div
      style={{
        padding: '12px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        borderBottom: !isLastItem ? `1px solid ${ABLE_COLORS.borderLight}` : 'none',
        boxSizing: 'border-box',
        width: '100%'
      }}
    >
      {/* Item name - tap to edit */}
      <div
        onClick={onStartEdit}
        className="inv-item-name"
        style={{
          flex: 1,
          minWidth: 0,
          fontSize: '14px',
          fontWeight: '500',
          color: ABLE_COLORS.textPrimary,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '6px'
        }}
      >
        <span style={{ 
          overflow: 'hidden', 
          textOverflow: 'ellipsis', 
          whiteSpace: 'nowrap' 
        }}>
          {item.name}
        </span>
        <Edit2 size={10} color={ABLE_COLORS.textMuted} style={{ flexShrink: 0 }} />
      </div>

      {/* Quantity - shows as text pill, click to reveal +/- buttons */}
      {showQtyButtons ? (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          flexShrink: 0
        }}>
          <button
            onClick={(e) => { e.stopPropagation(); onDecrement(); }}
            title="Decrease quantity"
            style={{
              width: '26px',
              height: '26px',
              borderRadius: '6px',
              border: `1px solid ${ABLE_COLORS.border}`,
              background: ABLE_COLORS.bgSubtle,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0
            }}
          >
            <Minus size={12} color={ABLE_COLORS.textMuted} />
          </button>
          <span style={{
            minWidth: '32px',
            textAlign: 'center',
            fontSize: '13px',
            fontWeight: '600',
            color: ABLE_COLORS.textPrimary
          }}>
            {parseQtyNumber(item.quantity)}
          </span>
          <button
            onClick={(e) => { e.stopPropagation(); onIncrement(); }}
            title="Increase quantity"
            style={{
              width: '26px',
              height: '26px',
              borderRadius: '6px',
              border: `1px solid ${ABLE_COLORS.border}`,
              background: ABLE_COLORS.bgSubtle,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0
            }}
          >
            <Plus size={12} color={ABLE_COLORS.textMuted} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setShowQtyButtons(false); }}
            title="Done"
            style={{
              width: '26px',
              height: '26px',
              borderRadius: '6px',
              border: 'none',
              background: ABLE_COLORS.primary,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
              marginLeft: '2px'
            }}
          >
            <Check size={12} color="white" />
          </button>
        </div>
      ) : (
        <button
          onClick={(e) => { e.stopPropagation(); setShowQtyButtons(true); }}
          title="Edit quantity"
          style={{
            padding: '4px 10px',
            borderRadius: '12px',
            border: `1px solid ${ABLE_COLORS.border}`,
            background: ABLE_COLORS.bgSubtle,
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: '600',
            color: ABLE_COLORS.textSecondary,
            flexShrink: 0
          }}
        >
          {parseQtyNumber(item.quantity)} {parseQtyUnit(item.quantity)}
        </button>
      )}

      {/* Freshness badge - shows for items that need attention */}
      {freshness.status !== 'unknown' && freshness.status !== 'fresh' && (
        <div
          className="inv-item-freshness"
          title={freshness.daysLeft !== null ? 
            (freshness.daysLeft <= 0 ? 'Expired' : `${freshness.daysLeft} day${freshness.daysLeft !== 1 ? 's' : ''} left`) : ''}
          style={{
            padding: '3px 8px',
            borderRadius: '12px',
            background: freshnessConfig.bgColor,
            color: freshnessConfig.color,
            fontSize: '11px',
            fontWeight: '600',
            flexShrink: 0,
            whiteSpace: 'nowrap',
            display: 'flex',
            alignItems: 'center',
            gap: '3px'
          }}
        >
          {FreshnessIcon && <FreshnessIcon size={10} />}
          {freshnessConfig.label}
        </div>
      )}

      {/* Category badge - hidden when grouped by category */}
      {!hideCategory && (
        <div
          className="inv-item-category"
          style={{
            padding: '4px 8px',
            borderRadius: '12px',
            background: `${config.color}15`,
            color: config.color,
            fontSize: '11px',
            fontWeight: '600',
            flexShrink: 0,
            whiteSpace: 'nowrap'
          }}
        >
          {config.label}
        </div>
      )}

      {/* Add to shopping button - shopping cart icon */}
      <button
        onClick={isOnShoppingList ? undefined : onAddToShopping}
        title={isOnShoppingList ? 'Already on shopping list' : 'Add to shopping list'}
        style={{
          width: '32px',
          height: '32px',
          borderRadius: '8px',
          border: 'none',
          background: isOnShoppingList ? ABLE_COLORS.bgSubtle : `${ABLE_COLORS.primary}15`,
          cursor: isOnShoppingList ? 'default' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          opacity: isOnShoppingList ? 0.5 : 1
        }}
      >
        {isOnShoppingList ? (
          <Check size={16} color={ABLE_COLORS.textMuted} />
        ) : (
          <ShoppingCart size={16} color={ABLE_COLORS.primary} />
        )}
      </button>

      {/* Delete button */}
      <button
        onClick={onRemove}
        title="Remove from pantry"
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '6px',
          borderRadius: '6px',
          display: 'flex',
          alignItems: 'center',
          flexShrink: 0
        }}
      >
        <Trash2 size={16} color={ABLE_COLORS.textMuted} />
      </button>
      
      <style>{`
        @media (max-width: 380px) {
          .inv-item-category { display: none !important; }
          .inv-item-freshness { display: none !important; }
        }
      `}</style>
    </div>
  );
}
